import { useMemo, useRef, useState } from 'react';
import type { SignTag } from '@todo/shared';

/**
 * Several tags on one document: chips plus a text box.
 *
 * Typing suggests the organisation's existing tags; Enter or a comma adds
 * what you typed (a new name is simply created when the document is saved),
 * Backspace on an empty box removes the last chip. Tags exist so a signing
 * can be found months later by what it was about - so reusing an existing
 * tag is made easier than inventing a near-duplicate.
 */
export function TagInput({
  value,
  onChange,
  known,
  placeholder = 'Add a tag and press Enter',
  disabled = false,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  known: SignTag[];
  placeholder?: string;
  disabled?: boolean;
}) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const have = useMemo(() => new Set(value.map((v) => v.toLowerCase())), [value]);
  const query = text.trim().replace(/^#+/, '').toLowerCase();
  const suggestions = useMemo(
    () => known
      .filter((t) => !have.has(t.name.toLowerCase()) && (query === '' || t.name.toLowerCase().includes(query)))
      .slice(0, 6),
    [known, have, query],
  );

  function add(raw: string) {
    const name = raw.replace(/^#+/, '').replace(/\s+/g, ' ').trim().slice(0, 40);
    if (!name || have.has(name.toLowerCase()) || value.length >= 10) { setText(''); return; }
    // An existing tag keeps its own spelling ("Audit", not "audit").
    const match = known.find((t) => t.name.toLowerCase() === name.toLowerCase());
    onChange([...value, match?.name ?? name]);
    setText('');
  }

  return (
    <div className="relative">
      <div
        className={`flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-1.5 focus-within:border-brand-500 ${disabled ? 'opacity-60' : ''}`}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
            #{t}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove tag ${t}`}
                onClick={(e) => { e.stopPropagation(); onChange(value.filter((x) => x !== t)); }}
                className="text-indigo-400 hover:text-indigo-700"
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          value={text}
          disabled={disabled || value.length >= 10}
          placeholder={value.length === 0 ? placeholder : value.length >= 10 ? 'Up to 10 tags' : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v.endsWith(',')) { add(v.slice(0, -1)); return; }
            setText(v);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => { setTimeout(() => setOpen(false), 120); if (text.trim()) add(text); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); if (text.trim()) add(text); }
            else if (e.key === 'Backspace' && text === '' && value.length > 0) onChange(value.slice(0, -1));
          }}
          className="min-w-[8rem] flex-1 bg-transparent px-1 py-0.5 text-sm outline-none"
        />
      </div>
      {open && suggestions.length > 0 && !disabled && (
        <ul className="absolute left-0 right-0 z-30 mt-1 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {suggestions.map((t) => (
            <li
              key={t.id}
              onMouseDown={(e) => { e.preventDefault(); add(t.name); }}
              className="flex cursor-pointer items-center justify-between px-2.5 py-1.5 text-sm text-slate-800 hover:bg-slate-100"
            >
              <span>#{t.name}</span>
              {t.documentCount !== undefined && (
                <span className="text-xs text-slate-400">{t.documentCount}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
